import { filter, fromEvent, Subscription } from "rxjs";
import Logger from '../../js/lib/logger';
import { activeInsert$, boardMode$, deleteInsert$, message$, snapObject$, trayData$, updateInsert$ } from "../lib/state";
import {  ModeType, Point, RectPoints, SnapObject, TrayData } from "../lib/types";
import { SVG } from '@svgdotjs/svg.js';
import { ICONS_URL, Measurement } from "../lib/constants";
import Insert from './Insert';
import { gsap, Draggable } from 'gsap/all';
import RectangleTransformer from './RectangleTransformer';
import { getDistance2D, rectContains, rectOverlaps } from "../lib/util.functions";

export default class SvgInsertsContainer extends HTMLElement {
  #NAME = 'SvgInsertsContainer';
  #subs: Subscription[] = [];
  #trayData?: TrayData;
  svg: SVGElement | null = null;
  #snapObject: SnapObject = { x: [], y: [] };
  #width: number = 0;
  #height: number = 0;
  #inserts: Insert[] = [];
  transformer: RectangleTransformer | null = null;
  #demoData: { nodes: Node[], tweens: gsap.core.Tween[] } | null = null;
  #first = true;

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    gsap.registerPlugin(Draggable);
  }

  connectedCallback(): void {
    Logger.info(`${this.#NAME} > connectedCallback`);
    this.#subs = [
      trayData$.subscribe(dta => {
        if (this.#trayData 
          && !this.#trayData.inserts.length 
          && dta.inserts.length === 1 
          && dta.inserts[0].type !== 'cushion'
          && dta.inserts[0].type !== 'pillow'
        ) {
          setTimeout(() => {
            this.#demo(this.#inserts[0]);
          }, 20);
        }
        this.#trayData = dta;
        if (this.#first) {
          setTimeout(() => {
            this.#build();
          }, 600);
        } else {
          this.#build();
        }
        /* this.#build(); */
      }),
      deleteInsert$.subscribe(() => {
        this.#killTransformer();
        activeInsert$.next(null);
      }),
      fromEvent(document, 'pointerdown')
        .pipe(filter(() => !!this.#demoData))
        .subscribe(() => this.#clearDemo())
    ]
  }

  disconnectedCallback(): void {
    this.#subs.forEach(sub => sub.unsubscribe());
  }

  static get observedAttributes(): string[] {
    return ['data-disabled'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'data-disabled') {
      if (newValue === 'true') {
        this.#inserts.forEach(insert => {
          Draggable.get(insert.element)?.disable();
        });
      } else {
        this.#inserts.forEach(insert => {
          Draggable.get(insert.element)?.enable();
        });
      }
    }
  }

  #clearDemo() {
    Logger.info(`${this.#NAME} > #clearDemo`);
    if (this.#demoData) {
      this.#demoData.nodes.forEach(n => this.svg?.removeChild(n));
      this.#demoData.tweens.forEach(t => t.kill());
      this.#demoData = null;
      message$.next({  show: false });
    }
  }

  #demo(insert: Insert) {
    Logger.info(`${this.#NAME} > demo`);
    const { x, y, width, height } = insert.data;
    activeInsert$.next(insert);
    insert.mode = 'transform';
    message$.next({ type: 'tip', show: true });
    setTimeout(() => {
      this.transformer = new RectangleTransformer(this.#snapObject);
      this.svg?.append(this.transformer.element);
      this.transformer.update({ 
        width, 
        height,
        x, 
        y 
      });
      this.transformer.show = true;
      const ta1 = SVG().use('transform-arrow', ICONS_URL).attr({ width: 36, height: 36, x: width - 18, y: (height / 2) - 18.5 });
      const ta2 = SVG().use('transform-arrow', ICONS_URL).attr({ width: 36, height: 36, x: (width / 2) - 18, y: height - 18 });
      this.svg?.append(ta1.node, ta2.node);
      gsap.set(ta2.node, { rotate: 90, transformOrigin: 'center' });
      this.#demoData = {
        nodes: [ta1.node, ta2.node],
        tweens: [
          gsap.fromTo(ta1.node, { xPercent: -10 }, { xPercent: 10, duration: 0.6, ease: 'power2.inOut', repeat: -1, yoyo: true}),
          gsap.fromTo(ta2.node, { yPercent: -30 }, { yPercent: 30, duration: 0.6, ease: 'power2.inOut', repeat: -1, yoyo: true})
        ]
      }
    }, 0);
  }

  #build() {
    Logger.info(`${this.#NAME} > build`);
    const w = ((this.#trayData?.width || 0) * Measurement.UNIT_PX) - Measurement.SEP_WIDTH_PX;
    const h = ((this.#trayData?.depth || 0) * Measurement.UNIT_PX) - Measurement.SEP_WIDTH_PX;

    if (this.#trayData && (w !== this.#width || h !== this.#height)) {
      Logger.info(`${this.#NAME} > #build > trayData`, this.#trayData);
      if (this.svg) this.svg.remove();
      if (this.transformer) this.transformer.remove();
      this.#width = w;
      this.#height = h;
      const numX = Math.round((this.#trayData.width - Measurement.SEP_WIDTH) / Measurement.UNIT_FRACTION) + 1;
      const numY = Math.round((this.#trayData.depth - Measurement.SEP_WIDTH) / Measurement.UNIT_FRACTION) + 1;
      this.#snapObject = { x: [], y: [] };
      //
      for (let i = 0; i < numX; i++) {
        this.#snapObject.x.push(i * Measurement.UNIT_FRACTION_PX);
      }
      //
      for (let i = 0; i < numY; i++) {
        this.#snapObject.y.push(i * Measurement.UNIT_FRACTION_PX);
      }
      Logger.info(`${this.#NAME} > snapObject >`, this.#snapObject);
      snapObject$.next(this.#snapObject);
      //
      SVG().addTo(this).attr({ id: 'svg-inserts-container', viewBox: `-11 -11 ${w + 22} ${h + 22}` });
      this.svg = this.querySelector('svg');
    }

    this.#trayData?.inserts.forEach(data => {
      let insert = this.#inserts.find(el => el.id === data.id);
      if (!insert) {
        insert = new Insert(data);
        this.#appendInsert(insert);
        this.#inserts.push(insert);
        if (!this.#first) updateInsert$.next(insert.data);
      } else {
        const curData = insert.data;
        if (!this.svg?.querySelector(`#${curData.id}`)) this.#appendInsert(insert);
        if (JSON.stringify(data) !== JSON.stringify(curData)) {
          insert.data = data;
        }
      }
    });
    //
    const ids = this.#trayData?.inserts.map(dta => dta.id);
    this.#inserts = this.#inserts.filter(insert => ids?.includes(insert.id));
    if (this.#first) this.#first = false;
  }

  #appendInsert(insert: Insert) {
    this.svg?.appendChild(insert.element);
    let startingPoint: Point = { x: insert.data.x, y: insert.data.y };
    //
    const instance = Draggable.create(insert.element, {
      type: 'x,y',
      bounds: '.svg-grid__bg',
      /* bounds: { 
        top: 0, 
        left: 0, 
        width: this.#width + 12, 
        height: this.#height + 12
      }, */
      liveSnap: this.#snapObject,
      activeCursor: 'grabbing',
      onPress: () => {
        this.#killTransformer();
        this.svg?.append(insert.element);
        activeInsert$.next(insert);
        const { x, y } = insert.data;
        startingPoint = { x, y };
        this.#inserts.filter(el => el.id !== insert.id).forEach(el => {
          el.mode = 'nuetral';
        });
        this.#hitTest(instance, insert, 'nuetral');
        message$.next({ show: false });
      },
      onMove: () => { 
        this.#hitTest(instance, insert, 'nuetral');
      },
      onRelease: (e: PointerEvent) => {
        e.preventDefault();
        /* Logger.info(`Draggable > onRelease`, insert.rect.bbox()); */
        insert.data = { ...insert.data, x: instance.x, y: instance.y };
        this.transformer = new RectangleTransformer(this.#snapObject);
        this.svg?.append(this.transformer.element);
        this.transformer.update({ 
          width: insert.data.width, 
          height: insert.data.height,
          x: instance.x, 
          y: instance.y 
        });
        this.#hitTest(instance, insert, 'transform', startingPoint, { x: instance.x, y: instance.y });
        updateInsert$.next({ ...insert.data, x: instance.x, y: instance.y });
      }
    })[0];
    //
    this.#hitTest(instance, insert, 'nuetral', this.#first ? startingPoint : undefined);
  }

  #killTransformer() {
    if (this.transformer) {
      this.transformer.remove();
      this.transformer = null;
    }
  }

  #hitTest(instance: globalThis.Draggable, insert: Insert, defaultMode: ModeType, startingPoint?: Point, endPoint?: Point) {
    let overlap = false;
    this.#inserts.filter(el => el.id !== insert.id).forEach(el => {
      if (instance.hitTest(el.element, 1)) overlap = true;
    });
    if (overlap && insert.mode !== 'error') {
      insert.mode = 'error';
    } else if (!overlap) {
      if (insert.mode !== defaultMode) insert.mode = defaultMode;
      if (this.transformer && insert.mode !== defaultMode) this.transformer.mode = defaultMode;
    }
    if (this.#first && overlap && startingPoint) {
      gsap.set(insert.element, {...startingPoint});
      insert.mode = defaultMode;
      return
    }
    if (this.transformer && defaultMode === 'transform') {
      if (insert.type !== 'cushion' && insert.type !== 'pillow') this.transformer.show = true;
      if (overlap) {
        /* console.log('overlap!!!!'); */
        this.transformer.mode = 'error';
        boardMode$.next(overlap ? 'error' : 'nuetral');
        message$.next({ type: 'overlap', show: true });
        const { width, height, id } = insert.data;
        const point = this.getNextAvailablePoint(width, height, id, startingPoint, endPoint);
        if (point) {
          gsap.to(insert.element, { 
            x: point.x, 
            y: point.y, 
            duration: 0.4, 
            delay: 1.2,
            ease: 'power3.in', 
            onStart: () => {
              if (this.transformer) this.transformer.show = false;
            },
            onComplete: () => {
              updateInsert$.next({ ...insert.data, x: point.x, y: point.y });
              boardMode$.next('nuetral');
              insert.mode = 'transform';
              if (this.transformer) {
                if (insert.type !== 'cushion' && insert.type !== 'pillow') this.transformer.show = true;
                this.transformer.mode = 'transform';
              }
              setTimeout(() => {
                message$.next({ show: false });
              }, 3000);
              
            }
          })
        }
      }
    }
  }

  getAvailablePoints(width: number, height: number, omitId?: string): Point[] {
    let availablePoints: Point[] = [];
    let x: number;
    let y: number;
    let a: RectPoints;
    let b: RectPoints;
    const maxX = this.#width - width;
    const maxY = this.#height - height;
    //
    for (let i = 0; i < this.#snapObject.x.length; i++) {
      x = this.#snapObject.x[i];
      if (x <= maxX) {
        for (let j = 0; j < this.#snapObject.y.length; j++) {
          y = this.#snapObject.y[j];
          if (y <= maxY) {
            availablePoints.push({ x, y });
          }
        }
      }
    }
    //
    availablePoints.sort((a,b) => a.y - b.y);
    //
    this.#trayData?.inserts.forEach((insert) => {
      if (omitId && insert.id === omitId) return;
      a = {
        x1: insert.x, 
        x2: insert.x + insert.width,
        y1: insert.y,
        y2: insert.y + insert.height
      }
      availablePoints = availablePoints.filter(p => {
        b = {
          x1: p.x, 
          x2: p.x + width,
          y1: p.y,
          y2: p.y + height
        }
        return !rectContains(a,b) && !rectOverlaps(a,b); 
      })
    });
    // console.log('availablePoints', availablePoints)
    return availablePoints;
  }
 
  getNextAvailablePoint(width: number, height: number, omitId?: string, startingPoint?: Point, endPoint?: Point): Point | undefined {
    const availablePoints = this.getAvailablePoints(width, height, omitId);
    let returnPoint: Point;
    if (endPoint && startingPoint) {
      const closetPoint = availablePoints.map((p, i) => ({index: i, dist: getDistance2D(endPoint, p)}))
                                  .reduce((a, b) => a.dist < b.dist ? a : b);
      // console.log('closetPoint', closetPoint);                          
      returnPoint = closetPoint.dist >= 120 ? startingPoint : availablePoints[closetPoint.index];
    } else {
      returnPoint = startingPoint ? (availablePoints.find(p => p.x === startingPoint.x && p.y === startingPoint.y)) || availablePoints[0] : availablePoints[0];
    }
    return returnPoint || availablePoints[0];
  }
}

customElements.define('svg-inserts-container', SvgInsertsContainer);